import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { sanitize } from 'dompurify'
import { Avatar, Box, Button, Chip, Grid, Stack, Typography } from '@mui/material'
import { LocationOn } from '@mui/icons-material'
import PersonIcon from '@mui/icons-material/Person'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import DescriptionIcon from '@mui/icons-material/Description'
import CandidateDetailSection from 'pages/Hospitals/Candidates/CandidateDetailSection'
import {
  formatYearsExperience,
  formatBoardEligibility,
  formatSearchStatus,
  getSearchStatusColor,
  formatAvailabilityDate,
  formatSpecialties,
  formatLanguages,
  formatVisas,
  formatPreferredStates,
  formatRelocationPreference,
  formatPracticeTypes,
  formatAreaTypes,
  formatAcademicPreference,
  formatGlobalRegions,
  formatLicensedStates,
  formatMemberDate,
} from 'pages/Hospitals/Candidates/candidateDisplayHelpers'
import { hostAddress } from 'data/helpers'
import { formatName } from 'common/helpers'
import { getLocation } from 'pages/Jobs/helpers'
import { getPhysicianProfileImage } from './helpers'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const DetailRow = ({ label, value }) => {
  if (!value) return null
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant='caption' color='text.secondary'>
        {label}
      </Typography>
      <Typography variant='body2'>{value}</Typography>
    </Box>
  )
}

const ChipList = ({ label, items }) => {
  if (!items) return null
  const list = items.split(', ')
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      <Stack direction='row' spacing={0.5} flexWrap='wrap' gap={0.5}>
        {list.map(item => (
          <Chip key={item} label={item} size='small' variant='outlined' />
        ))}
      </Stack>
    </Box>
  )
}

const cardStyles = {
  backgroundColor: '#FFFFFF',
  border: '1px solid',
  borderColor: 'grey.200',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  borderRadius: 2,
}

const PhysicianContent = ({ item = {} }) => {
  const dispatch = useDispatch()
  const { email: identityEmail, employerId } = useSelector(state => state.identity)

  const resume = item.resume || {}
  const handleResumeClick = useCallback(() => {
    if (!resume.key) return
    fetch(hostAddress(`/api/storage/resume?key=${resume.key}&fileName=${resume.fileName}`), {
      method: 'GET',
      headers: new Headers({ 'content-type': 'application/json' }),
    }).then(response => response.blob().then(blob => window.open(URL.createObjectURL(blob))))
  }, [resume.key, resume.fileName])

  const handleContactClick = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'physician_contact_click',
        eventType: ProductAnalyticsEventTypes.BUTTON_CLICK,
        properties: { physicianUserId: item.user?.id || item.id },
        distinctId: identityEmail,
        employerId: employerId,
      }),
    )
  }

  const firstName = item.user?.firstName || item.firstName
  const lastName = item.user?.lastName || item.lastName
  const name = `${formatName(firstName)} ${formatName(lastName)}`
  const profileImageUrl = getPhysicianProfileImage(item.user?.profileImage)

  const city = item.city || item.user?.city
  const state = item.state || item.user?.state
  const location = getLocation(city, state)

  const showEmail = item.showEmail || item.user?.showEmail
  const email = item.user?.email || item.email
  const specialty = item.specialties?.[0]?.name
  const yearsLabel = formatYearsExperience(item.yearsExperience)
  const headline = [specialty, yearsLabel ? `${yearsLabel} Years` : null].filter(Boolean).join(' | ')

  const searchStatusLabel = formatSearchStatus(item.searchStatus)
  const searchStatusColor = getSearchStatusColor(item.searchStatus)

  const residencyDateRange =
    item.residencyStart || item.residencyEnd
      ? [
          item.residencyStart
            ? new Date(item.residencyStart).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
            : null,
          item.residencyEnd
            ? new Date(item.residencyEnd).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
            : null,
        ]
          .filter(Boolean)
          .join(' – ')
      : null
  const residencyDisplay = [item.residency, residencyDateRange].filter(Boolean).join(' — ')

  return (
    <Box sx={{ p: 2 }}>
      <Box
        sx={{
          width: '100%',
          py: 2,
          px: 2,
          bgcolor: '#FFFFFF',
          borderBottom: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          mb: 2,
        }}
      >
        <Stack direction='row' spacing={2} alignItems='flex-start'>
          <Avatar
            src={profileImageUrl}
            sx={{
              width: 64,
              height: 64,
              flexShrink: 0,
              border: '3px solid',
              borderColor: '#FFFFFF',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction='row' spacing={0.5} alignItems='center' flexWrap='wrap'>
              <Typography variant='h5' fontWeight={700}>
                {name}
              </Typography>
            </Stack>
            {headline && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 0.5 }}>
                {headline}
              </Typography>
            )}
            <Stack direction='row' spacing={1} flexWrap='wrap' gap={1} sx={{ mt: 1 }}>
              {location && (
                <Chip icon={<LocationOn />} label={location} variant='outlined' size='small' />
              )}
              {searchStatusLabel && (
                <Chip label={searchStatusLabel} color={searchStatusColor} size='small' sx={{ fontWeight: 600 }} />
              )}
            </Stack>
          </Box>
          {showEmail && email && (
            <a href={`mailto:${email}`} style={{ textDecoration: 'none' }}>
              <Button variant='contained' size='small' onClick={handleContactClick} sx={{ ml: 'auto', flexShrink: 0 }}>
                Contact
              </Button>
            </a>
          )}
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          {(item.about || item.aboutRaw || item.aboutJob) && (
            <CandidateDetailSection icon={<PersonIcon color='action' />} title='About' sx={cardStyles}>
              {(item.about || item.aboutRaw) && (
                <Box
                  sx={{ mb: item.aboutJob ? 2 : 0 }}
                  dangerouslySetInnerHTML={{ __html: sanitize(item.about || item.aboutRaw) }}
                />
              )}
              {item.aboutJob && (
                <>
                  <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 0.5 }}>
                    What I'm Looking For
                  </Typography>
                  <Box dangerouslySetInnerHTML={{ __html: sanitize(item.aboutJob) }} />
                </>
              )}
            </CandidateDetailSection>
          )}

          <CandidateDetailSection icon={<MedicalServicesIcon color='action' />} title='Qualifications' sx={cardStyles}>
            <DetailRow label='Candidate Type' value={item.candidateType?.name} />
            <ChipList label='Specialties' items={formatSpecialties(item.specialties)} />
            <ChipList label='Subspecialties' items={formatSpecialties(item.subspecialties)} />
            <DetailRow label='Board Eligibility' value={formatBoardEligibility(item.boardEligibility)} />
            <DetailRow label='NPI Number' value={item.npiNumber} />
            {resume.key && (
              <Box sx={{ mb: 1 }}>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.5 }}>
                  Resume
                </Typography>
                <Button startIcon={<DescriptionIcon />} variant='outlined' size='small' onClick={handleResumeClick}>
                  View Resume
                </Button>
              </Box>
            )}
            <DetailRow label='Years of Experience' value={formatYearsExperience(item.yearsExperience)} />
            <ChipList label='Licensed States' items={formatLicensedStates(item.licensedStates)} />
            <DetailRow label='Residency' value={residencyDisplay || null} />
            <DetailRow label='Languages' value={formatLanguages(item.candidateLanguages)} />
            <ChipList label='Visa Types' items={formatVisas(item.visas)} />
          </CandidateDetailSection>

          <CandidateDetailSection icon={<EventAvailableIcon color='action' />} title='Preferences' sx={cardStyles}>
            <DetailRow label='Search Status' value={formatSearchStatus(item.searchStatus)} />
            <DetailRow label='Available' value={formatAvailabilityDate(item.availabilityDate)} />
            <DetailRow label='Member Since' value={formatMemberDate(item.createdAt)} />
            <Box sx={{ my: 1.5, height: '1px', backgroundColor: 'grey.200' }} />
            <DetailRow label='Preferred City' value={item.preferredCity} />
            <ChipList label='Preferred States' items={formatPreferredStates(item.preferredStates)} />
            <DetailRow label='Relocation' value={formatRelocationPreference(item.relocationPreference)} />
            <ChipList label='Preferred Area Types' items={formatAreaTypes(item.preferredAreaTypes)} />
            <ChipList label='Practice Types' items={formatPracticeTypes(item.preferredPracticeTypes)} />
            <DetailRow label='Academic Preference' value={formatAcademicPreference(item.academicPreference)} />
            <ChipList label='Global Regions' items={formatGlobalRegions(item.preferredGlobalRegions)} />
          </CandidateDetailSection>
        </Grid>
      </Grid>
    </Box>
  )
}

export default PhysicianContent
